import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtDate } from "../lib/api";
import { Landmark } from "lucide-react";
import { toast, Toaster } from "sonner";

const Loans = () => {
  const [products, setProducts] = useState([]);
  const [apps, setApps] = useState([]);
  const [open, setOpen] = useState(null); // product
  const [amount, setAmount] = useState("");
  const [term, setTerm] = useState("");
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [p, a] = await Promise.all([api.get("/loans/products"), api.get("/loans/applications")]);
    setProducts(p.data); setApps(a.data);
  };
  useEffect(() => { load(); }, []);

  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/loans/applications", {
        product_id: open.id,
        amount: parseFloat(amount),
        term_months: parseInt(term),
        purpose,
      });
      toast.success("Application submitted. Pending review.");
      setOpen(null); setAmount(""); setTerm(""); setPurpose("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Application failed");
    } finally { setBusy(false); }
  };

  const statusColor = (s) =>
    s === "approved" ? "text-[hsl(var(--primary))]" :
    s === "rejected" ? "text-[hsl(var(--destructive))]" :
    "text-[hsl(var(--accent))]";

  return (
    <div className="space-y-10">
      <Toaster richColors position="top-right" />
      <div>
        <div className="overline">Credit desk</div>
        <h1 className="font-display text-4xl mt-1">Loans</h1>
      </div>

      <section>
        <h2 className="font-display text-2xl mb-4">Loan products</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[hsl(var(--border))] border border-[hsl(var(--border))]">
          {products.map((p) => (
            <div key={p.id} className="bg-[hsl(var(--surface))] p-6" data-testid={`product-${p.id}`}>
              <Landmark className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.4} />
              <h3 className="font-display text-lg mt-4">{p.name}</h3>
              <p className="text-sm text-[hsl(var(--text-muted))] mt-2 min-h-[3em]">{p.description}</p>
              <div className="mt-4 font-mono text-2xl">{p.interest_rate}% <span className="text-xs text-[hsl(var(--text-muted))]">APR</span></div>
              <div className="text-xs text-[hsl(var(--text-muted))] mt-1 font-mono">
                {fmtUSD(p.min_amount)} – {fmtUSD(p.max_amount)} · {p.min_term_months}-{p.max_term_months} mo
              </div>
              <button onClick={() => { setOpen(p); setAmount(p.min_amount); setTerm(p.min_term_months); }}
                className="mt-5 w-full border border-[hsl(var(--primary))] text-[hsl(var(--primary))] py-2 text-sm hover:bg-[hsl(var(--primary))] hover:text-white"
                data-testid={`apply-${p.id}`}>Apply</button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-4">Your applications</h2>
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
          <table className="w-full text-sm">
            <thead className="border-b border-[hsl(var(--border))]">
              <tr className="text-left">
                <th className="overline py-3 px-4">Submitted</th>
                <th className="overline py-3 px-4">Product</th>
                <th className="overline py-3 px-4 text-right">Amount</th>
                <th className="overline py-3 px-4">Term</th>
                <th className="overline py-3 px-4 text-right">Monthly</th>
                <th className="overline py-3 px-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} className="border-b border-[hsl(var(--border))]" data-testid={`app-${a.id}`}>
                  <td className="py-3 px-4 font-mono text-xs">{fmtDate(a.created_at)}</td>
                  <td className="py-3 px-4">{a.product_name}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmtUSD(a.amount)}</td>
                  <td className="py-3 px-4">{a.term_months} mo</td>
                  <td className="py-3 px-4 text-right font-mono">{fmtUSD(a.monthly_payment)}</td>
                  <td className={`py-3 px-4 capitalize font-medium ${statusColor(a.status)}`}>{a.status}</td>
                </tr>
              ))}
              {apps.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-[hsl(var(--text-muted))]">No applications yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setOpen(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={apply} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-8 w-full max-w-lg space-y-5">
            <div>
              <div className="overline">Apply for</div>
              <h2 className="font-display text-2xl mt-1">{open.name}</h2>
              <div className="text-sm text-[hsl(var(--text-muted))] mt-1">{open.interest_rate}% APR</div>
            </div>
            <div>
              <label className="overline block mb-2">Amount (USD)</label>
              <input type="number" min={open.min_amount} max={open.max_amount} step="100" required value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))] font-mono"
                data-testid="loan-amount" />
            </div>
            <div>
              <label className="overline block mb-2">Term (months)</label>
              <input type="number" min={open.min_term_months} max={open.max_term_months} required value={term}
                onChange={(e) => setTerm(e.target.value)}
                className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))] font-mono"
                data-testid="loan-term" />
            </div>
            <div>
              <label className="overline block mb-2">Purpose</label>
              <input required value={purpose} onChange={(e) => setPurpose(e.target.value)}
                className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))]"
                data-testid="loan-purpose" />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setOpen(null)} className="border border-[hsl(var(--border))] px-4 py-2 text-sm" data-testid="cancel-apply-btn">Cancel</button>
              <button type="submit" disabled={busy} className="bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm" data-testid="submit-apply-btn">
                {busy ? "Submitting…" : "Submit application"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Loans;
